/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  View,
  Modal,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Dimensions,
  TextInput,
  Linking,
} from "react-native";
import CloseIcon from "@assets/svg/CloseCircleGrayishBg";
import metrics, { normalize } from "@constants/metrics";
import Text from "@components/AppText";
import Button from "@components/Button";
import StarChecked from "@assets/svg/StarChecked";
import StarUnChecked from "@assets/svg/StarUnChecked";
import color from "@constants/color";
import { useDispatch, useSelector } from "react-redux";
import SuccessIcon from "@assets/svg/SuccessIcon";
import CheckGreen from "@assets/svg/CheckGreen";
import { STATE_STATUS } from "@reducers/actionTypes";
import { addRatingReviewForOrderNew } from "@services/api";
import analytics from "@services/analytics";
import { actions as delayedDataActions } from "@reducers/delayDataReducer";
import FetchCMSEntity from "@components/CMS/FetchCMSEntity";
import format from "date-fns/format";
import { APP_PLAY_STORE_URL } from "@constants/strings";
import { ORDER_FLOW_EVENTS } from "../../constants/analyticsEventsCT";

const { height } = Dimensions.get("window");

const SHOW_IF_LESS_THAN_RATING = 5;
const FEEDBACK_BOX_MAX_LENGTH = 100;
const CMS_KEY = "ORDER_RATE_SUCCESS_PAGE";

const DeliveryRatingModal = (props) => {
  const insets = useSafeAreaInsets();
  const dispatch = useDispatch();
  const {
    status,
    deliveryRatingDetails: {
      deliveryRatingTemplate = {},
      shouldShowDeliveryRatingModal: showRatingModal = false,
    },
  } = useSelector((state) => state.delayedDataReducer);

  const { user } = useSelector((state) => ({
    user: state.auth.user,
  }));

  const cmsPoweredEntities = useSelector(
    (s) => s?.configReducer?.appSettings?.cmsPoweredEntities,
  );

  const [rating, setRating] = useState(0);
  const [feedbackText, setFeedbackText] = React.useState("");
  const [review, setReview] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);

  const isAvailableForRating =
    !!deliveryRatingTemplate ||
    !!deliveryRatingTemplate?.deliveryRatingId ||
    !!showRatingModal;

  useEffect(() => {
    rating && addReview({ review_reasons: review });
  }, [review.length]);

  const {
    modalHeading,
    deliveryStatus,
    deliveredDate,
    ReasonsHeading,
    starLabels,
    reasons,
    submitButtonLabel,
    feedbackPlaceholder,
    feedbackLabel,
    submitButtonLabelOn5Star,
    deliveryRatingId,
    successSubmitLabels,
  } = deliveryRatingTemplate || {};

  const CT_EVENT_PAYLOAD = {
    delivery_rating_id: deliveryRatingId,
    edd: deliveredDate,
    user_id: user.user_id,
    time: format(new Date(), "dd/MM/yyyy hh:mm:ss"),
  };

  useEffect(() => {
    if (isAvailableForRating && !!deliveryRatingId && showRatingModal) {
      analytics.trackEvent(
        ORDER_FLOW_EVENTS.DELIVERY_RATING_POPUP_TRIGGER,
        CT_EVENT_PAYLOAD,
      );
    }
  }, [deliveryRatingId]);

  const loading = status === STATE_STATUS.FETCHING;

  const handleRatingChange = (index) => {
    const newIndex = rating === index ? index - 1 : index;
    setRating(newIndex);
    setReview([]);
    addReview({ review_reasons: [], review_star: newIndex });
  };

  const addReview = async (
    dataToSend = { deliveryRatingId },
    cb = () => {},
  ) => {
    const payload = { ...dataToSend, deliveryRatingId };
    addRatingReviewForOrderNew(payload)
      .then((res) => {
        if (typeof cb === "function") cb(res);
      })
      .catch((e) => console.log(e));
  };

  const handleSubmit = async () => {
    if (rating === SHOW_IF_LESS_THAN_RATING) {
      analytics.trackEvent(
        ORDER_FLOW_EVENTS.PLAY_STORE_REDIRECT,
        CT_EVENT_PAYLOAD,
      );
      setRating(0);
      dispatch(delayedDataActions.resetDeliveryReview());
      Linking.openURL(APP_PLAY_STORE_URL);
      return;
    }
    analytics.trackEvent(
      ORDER_FLOW_EVENTS.DELIVERY_RATING_SUBMITED,
      CT_EVENT_PAYLOAD,
    );
    addReview({ feedback: feedbackText }, setShowShareModal(true));
  };

  const closeModal = () => {
    setRating(0);
    dispatch(delayedDataActions.resetDeliveryReview());
    addRatingReviewForOrderNew({ decline: true, deliveryRatingId }).catch((e) =>
      console.log(e),
    );
  };

  const _renderReasons = () => {
    if (rating === 0 || rating >= SHOW_IF_LESS_THAN_RATING) return null;
    return (
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          marginBottom: normalize(5),
        }}
      >
        {Array.isArray(reasons) &&
          reasons.map((item) => {
            const isSelected = review.includes(item.value);
            return (
              <TouchableOpacity
                testID="drm-set-review"
                onPress={() => {
                  if (isSelected) {
                    setReview(review.filter((value) => value !== item.value));
                  } else {
                    setReview([...review, item.value]);
                  }
                }}
                style={{
                  paddingVertical: 7,
                  paddingHorizontal: 16,
                  paddingBottom: 4,
                  borderWidth: 1,
                  borderColor: "rgba(0, 0, 0, 0.5)",
                  borderRadius: 40,
                  marginVertical: 6,
                  marginRight: 6,
                  backgroundColor: isSelected
                    ? "rgba(0, 0, 0, 1)"
                    : "rgba(0, 0, 0, 0.02)",
                }}
              >
                <Text
                  style={{
                    color: isSelected ? "#f7f7f7" : "rgba(0, 0, 0, 1)",
                    fontSize: 12,
                    fontFamily: "NotoSansDevanagari-Regular",
                  }}
                >
                  {item.label}
                </Text>
              </TouchableOpacity>
            );
          })}
      </View>
    );
  };

  const _renderFeedbackBox = () => {
    if (!rating || rating === 5 || showShareModal) return null;
    return (
      <View>
        <View
          style={{
            marginBottom: 12,
          }}
        >
          <Text
            style={{
              color: "rgba(0, 0, 0, 0.86)",
              fontSize: 14,
              fontFamily: "NotoSansDevanagari-SemiBold",
              lineHeight: 24,
            }}
          >
            {feedbackLabel}
          </Text>
        </View>
        <View>
          <View
            style={{
              height: 90,
              borderRadius: 6,
              borderWidth: 1,
              borderColor: "#e6e6e6f0",
              marginTop: 5,
              paddingHorizontal: 5,
            }}
          >
            <TextInput
              testID="drm-feedback-box"
              placeholder={feedbackPlaceholder}
              value={feedbackText}
              multiline
              maxLength={FEEDBACK_BOX_MAX_LENGTH}
              numberOfLines={4}
              onChangeText={setFeedbackText}
              style={{
                fontSize: 12,
                fontFamily: "NotoSansDevanagari-Regular",
                textAlignVertical: "top",
                height: "100%",
                width: "100%",
              }}
            />
          </View>
        </View>
        <View style={{ marginTop: 4 }}>
          <Text
            style={{
              textAlign: "right",
              fontFamily: "NotoSansDevanagari-Regular",
              fontSize: 12,
              color: "rgba(0, 0, 0, 0.56)",
            }}
          >
            {feedbackText.length}/{FEEDBACK_BOX_MAX_LENGTH}
          </Text>
        </View>
      </View>
    );
  };

  // return if no order is pending for rating
  if (!isAvailableForRating) return null;

  return (
    <Modal
      statusBarTranslucent
      navigationBarTranslucent
      transparent
      animationType="slide"
      hardwareAccelerated
      onRequestClose={() => closeModal()}
      visible={showRatingModal}
    >
      {showShareModal && (
        <View
          style={{
            height: normalize(height * (30 / 100)),
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
        />
      )}
      <View
        style={{
          flex: 1,
          justifyContent: "flex-end",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
      >
        <View style={[styles.content, { paddingBottom: 20 + insets.bottom }]}>
          <ScrollView showsVerticalScrollIndicator={false}>
            <View
              style={{
                paddingTop: metrics.doubleBaseMargin,
                paddingBottom: 0,
              }}
            >
              <View style={styles.flexDRow}>
                {!showShareModal ? (
                  <Text fontType="head1" style={styles.modalHeading}>
                    {modalHeading}
                  </Text>
                ) : null}
                <View
                  style={{
                    position: "absolute",
                    right: 0,
                  }}
                >
                  <TouchableOpacity
                    testID="drm-close-modal"
                    style={{ marginTop: 5 }}
                    onPress={() => closeModal()}
                  >
                    <CloseIcon />
                  </TouchableOpacity>
                </View>
              </View>
            </View>
            {!showShareModal ? (
              <View style={{ flexDirection: "row" }}>
                <CheckGreen />
                <Text style={styles.deliveryStatus}> {deliveryStatus}</Text>
                <Text style={styles.deliveredDate}> | {deliveredDate}</Text>
              </View>
            ) : null}
            {!showShareModal && (
              <View>
                <View style={{ marginTop: 10 }}>
                  {rating && rating > 0 ? (
                    <Text
                      style={{
                        textAlign: "center",
                        color: "rgba(0, 0, 0, 0.86)",
                        fontFamily: "NotoSansDevanagari-SemiBold",
                        fontSize: 18,
                        marginTop: 14,
                      }}
                    >
                      {starLabels[rating]}
                    </Text>
                  ) : null}
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-around",
                      marginTop: 14,
                      marginBottom: 36,
                    }}
                  >
                    {[1, 2, 3, 4, 5].map((x, index) => {
                      if (x > rating) {
                        return (
                          <TouchableOpacity
                            testID="drm-star-unchecked"
                            key={x}
                            onPress={() => handleRatingChange(x)}
                          >
                            <StarUnChecked />
                          </TouchableOpacity>
                        );
                      }
                      return (
                        <TouchableOpacity
                          testID="drm-star-checked"
                          key={x}
                          onPress={() => handleRatingChange(x)}
                        >
                          <StarChecked />
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
                {rating !== 0 && (
                  <View
                    style={{
                      marginBottom: 12,
                    }}
                  >
                    <Text
                      style={{
                        color: "rgba(0, 0, 0, 0.86)",
                        fontSize: 14,
                        fontFamily: "NotoSansDevanagari-SemiBold",
                        lineHeight: 24,
                      }}
                    >
                      {rating >= SHOW_IF_LESS_THAN_RATING
                        ? null
                        : ReasonsHeading}
                    </Text>
                  </View>
                )}
                {loading && rating !== 0 ? (
                  <ActivityIndicator color={color.primary} size="small" />
                ) : (
                  _renderReasons()
                )}
              </View>
            )}
            {_renderFeedbackBox()}
            {showShareModal && (
              <>
                <View
                  style={{
                    flex: 1,
                    flexDirection: "row",
                    justifyContent: "center",
                  }}
                >
                  <View
                    style={{
                      marginTop: normalize(20),
                      marginBottom: normalize(30),
                      alignItems: "center",
                    }}
                  >
                    <SuccessIcon />
                    <Text
                      style={{
                        marginVertical: normalize(10),
                        fontFamily: "NotoSansDevanagari-SemiBold",
                        fontSize: 18,
                      }}
                    >
                      {successSubmitLabels?.main}
                    </Text>
                    <Text
                      style={{
                        color: color.lightBlack,
                        fontFamily: "NotoSansDevanagari-Regular",
                        fontSize: 12,
                      }}
                    >
                      {successSubmitLabels?.sub}
                    </Text>
                  </View>
                </View>
                {cmsPoweredEntities &&
                typeof cmsPoweredEntities === "object" &&
                CMS_KEY in cmsPoweredEntities ? (
                  <FetchCMSEntity
                    id={cmsPoweredEntities[CMS_KEY]}
                    type="BANNER"
                  />
                ) : null}
              </>
            )}
          </ScrollView>
        </View>
        {!showShareModal && (
          <View
            elevation={5}
            style={{
              padding: metrics.baseGutter,
              backgroundColor: "#fff",
            }}
          >
            <Button
              isSubmitting={false}
              disabled={rating === 0}
              style={{
                padding: 15,
              }}
              buttonWidth="100%"
              btnText={
                rating === 5 ? submitButtonLabelOn5Star : submitButtonLabel
              }
              buttonFontType="head1"
              height={56}
              borderRadius={10}
              onPress={handleSubmit}
            />
          </View>
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  flexDRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  content: {
    backgroundColor: "#fff",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 16,
    paddingBottom: 20,
  },
  modalHeading: {
    fontFamily: "NotoSansDevanagari-SemiBold",
    fontSize: 18,
    color: "rgba(0, 0, 0, 0.86)",
  },
  deliveryStatus: {
    color: "#27AE60",
    fontFamily: "NotoSansDevanagari-Regular",
    fontWeight: "bold",
    fontSize: 16,
  },
  deliveredDate: {
    fontFamily: "NotoSansDevanagari-Regular",
    fontSize: 16,
    color: "#707070",
  },
});

export default DeliveryRatingModal;
